import React from 'react';
import { Truck, Leaf, Loader2 } from 'lucide-react';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { RelayPointSelector } from '../RelayPointSelector';
import { useCurrency } from '../CurrencySelector';

export const ShippingSelector = ({
  quotes, quotesLoading, shippingMethod, onSelectCarrier,
  selectedQuote, relayPoint, onRelayChange, address, language,
}) => {
  const { format } = useCurrency();
  return (
  <>
    <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
      <Truck className="w-5 h-5 text-[#0F4C5C]" />
      {language === 'fr' ? 'Mode de livraison' : 'Shipping Method'}
    </h3>
    <p className="text-xs text-muted-foreground mb-4">
      {language === 'fr'
        ? `Tarifs calculés en temps réel pour ${address.country || 'FR'} selon le poids et le sous-total.`
        : `Real-time rates for ${address.country || 'FR'} based on weight & subtotal.`}
    </p>

    {quotesLoading ? (
      <div className="flex items-center justify-center py-8 bg-slate-50 rounded-lg" data-testid="shipping-quotes-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#0F4C5C] mr-2" />
        <span className="text-sm text-muted-foreground">
          {language === 'fr' ? 'Calcul des frais de transport…' : 'Calculating shipping…'}
        </span>
      </div>
    ) : quotes.length === 0 ? (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="shipping-quotes-empty">
        {language === 'fr'
          ? "Impossible de calculer les frais pour cette destination. Veuillez vérifier l'adresse."
          : 'Cannot compute rates for this destination. Please check the address.'}
      </div>
    ) : (
      <RadioGroup value={shippingMethod} onValueChange={onSelectCarrier} className="space-y-3" data-testid="shipping-quotes-list">
        {quotes.map((q) => (
          <div
            key={q.carrier}
            className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors ${
              shippingMethod === q.carrier ? 'border-[#0F4C5C] bg-[#0F4C5C]/5' : 'border-border hover:border-[#0F4C5C]/50'
            }`}
            onClick={() => onSelectCarrier(q.carrier)}
            data-testid={`shipping-carrier-${q.carrier}`}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <RadioGroupItem value={q.carrier} id={`carrier-${q.carrier}`} />
              {q.logo_url && (
                <img src={q.logo_url} alt={q.name} className="h-7 w-auto object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }} />
              )}
              <div className="min-w-0">
                <Label htmlFor={`carrier-${q.carrier}`} className="cursor-pointer font-medium block truncate">
                  {q.name}
                  {q.type === 'relay' && (
                    <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                      {language === 'fr' ? 'Point relais' : 'Pickup point'}
                    </span>
                  )}
                </Label>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                  <span>{language === 'fr' ? 'Livraison' : 'Delivery'} : {q.eta_days} {language === 'fr' ? 'jours' : 'days'}</span>
                  {q.carbon_offset && (
                    <span className="inline-flex items-center gap-1 text-emerald-700">
                      <Leaf className="w-3 h-3" /> CO₂ {language === 'fr' ? 'compensé' : 'offset'}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <span className={`font-semibold whitespace-nowrap ml-3 ${q.free ? 'text-green-600' : ''}`}>
              {q.free ? (language === 'fr' ? 'Gratuit' : 'Free') : format(q.cost)}
            </span>
          </div>
        ))}
      </RadioGroup>
    )}

    {selectedQuote?.type === 'relay' && shippingMethod === selectedQuote.carrier && (
      <RelayPointSelector
        postalCode={address.postal_code}
        city={address.city}
        country={address.country}
        carrier={selectedQuote.carrier}
        value={relayPoint}
        onChange={onRelayChange}
        language={language}
      />
    )}
  </>
  );
};
